import {Component, FontAwesomeIcon} from 'substance'
import {isObject, isArray} from 'lodash'
import md5 from 'md5'

/**
 * @typedef UIAvatar.props
 * @property {string} avatarId - ID of the avatar to display
 * @property {string} [avatarSource] - "gravatar"
 * @property {string} avatarAlt - Alt text
 */

/**
 * @typedef UIAvatar.state
 * @property {Boolean} avatarIsLoaded=false
 * @property {Boolean} loadHasError=false
 */

/**
 * A user avatar component
 *
 * Can show user images from Gravatar, or a font awesome icon.
 *
 * @name UIAvatar
 * @class
 *
 * @example
 * import {UIAvatar} from 'writer'
 *
 * render($$) {
 *     const el = $$('div')
 *
 *     return el.append(
 *         $$(UIAvatar, {
 *           avatarSource: 'gravatar',
 *           avatarId: 'test.testsson@test.com',
 *           avatarAlt: 'Alternative text'
 *       })
 *     )
 * }
 *
 * @param {UIAvatar.props} props
 * @property {UIAvatar.props} props
 * @property {UIAvatar.state} state
 */
class AvatarComponent extends Component {

    getInitialState() {
        return {
            avatarIsLoaded: false,
            loadHasError: false
        }
    }

    shouldRerender(newProps, newState) {
        return newState.avatarIsLoaded !== this.state.avatarIsLoaded
    }

    render($$) {

        const {avatarId, avatarSource, avatarAlt} = this.props
        const el = $$('span').addClass('avatar__container')
        let contentElement = this._getDummyAvatarComponent($$)

        /**
         * We no longer support Twitter avatars since we retired "ConceptBackend"
         * As of now we dont have anywhere to login to twitters api with oAuth and generate a link anymore
         * If this turns out to not be an issue, maybe we will re-work this more, but if twitter is really needed
         * we will have to find a new backend to generate the link
         */

        if (avatarSource === 'twitter') {
            console.warn('avatarSource: \'twitter\' is deprecated, use avatarSource: \'gravatar\' instead')
        }

        if (avatarSource === 'gravatar' && avatarId) {
            const gravatarUrl = this.generateGravatarUrl(avatarId)
            if (this.state.avatarIsLoaded) {
                contentElement = $$('img').addClass('avatar').attr('src', gravatarUrl)
            } else {
                this.prefetchGravatarImage(gravatarUrl)
            }
        }

        if (avatarAlt) {
            contentElement.attr('alt', avatarAlt)
        }

        return el.append(contentElement).ref(`avatarComponent-${avatarId}`)
    }

    prefetchGravatarImage(gravatarUrl) {
        fetch(gravatarUrl)
            .then(response => this.context.api.router.checkForOKStatus(response))
            .then(() => {
                this.setState({
                    avatarIsLoaded: true,
                    loadHasError: false,
                    avatarUrl: gravatarUrl
                })
            })
            .catch(() => {
                // Could not pre-load gravatar
            })
    }

    generateGravatarUrl(email) {
        const hashedEmail = md5(email)
        return `https://www.gravatar.com/avatar/${hashedEmail}?d=404`
    }

    /**
     * Returns a component with a Font Awesome showing the fa-user icon
     * @returns {Component} A substance FontAwesomeIcon
     * @private
     */
    _getDummyAvatarComponent($$) {
        return $$(FontAwesomeIcon, {icon: 'fa-user'}).addClass('avatar--simple')
    }

    /**
     * Triggers an AJAX request to fetch the image url
     * for a avatarId through the concepts backend server
     * If it success it sets the url in the state.
     * If failes if sets the state with error message and error status
     *
     * @private
     * @deprecated 2018-11-15
     * @param {string} avatarId - The Twitter handle
     */
    loadAvatarUrlForAvatarId(avatarId) {
        console.warn('loadAvatarUrlForAvatarId(avatarId) is deprecated, use avatarSource: \'gravatar\' instead')

        if (!avatarId) {
            this.setState({
                avatarIsLoaded: true,
                loadHasError: true
            })
        }

        this.context.api.router.get(`/api/concepts/author/${avatarId}/thumbnail`)
            .then(response => this.context.api.router.checkForOKStatus(response))
            .then(response => response.text())
            .then(url => {
                this.setState({
                    avatarIsLoaded: true,
                    loadHasError: false,
                    avatarUrl: url
                })
            })
            .catch((error) => {
                this.setState({
                    avatarIsLoaded: true,
                    loadHasError: true,
                    errorMessage: error
                })
            })
    }

    /**
     * Fetches the Twitter url for an author
     * @returns {string|undefined}
     * @private
     */
    static _getTwitterUrlFromAuthorLink(link) {
        console.warn('_getTwitterUrlFromAuthorLink(link) is deprecated, use avatarSource: \'gravatar\' instead')
        return AvatarComponent.findAttribute(link, '@url')
    }

    /**
     * Misspelled. Fetches the Twitter url for an author
     * @returns {string|undefined}
     * @private
     * @deprecated 2018-11-15
     */
    static _getTwitterUrlFromAuhtorLink(link) {
        console.warn('_getTwitterUrlFromAuhtorLink(link) is deprecated, use avatarSource: \'gravatar\' instead')
        return AvatarComponent._getTwitterUrlFromAuthorLink(link)
    }

    /**
     * Finds a links object for a specific type.
     * Checks if it's array or and object.
     * @private
     */
    static _getLinkForType(links, type) {
        if (!links) {
            return undefined
        }
        if (isObject(links) && links['@type'] === type) {
            return links
        } else if (isArray(links)) {
            return links.find((link) => {
                return link['@type'] === type
            })
        }

    }

    /**
     * Returns the Twitter handle from an URL
     * @private
     */
    static _getTwitterHandleFromTwitterUrl(url) {
        console.warn('_getTwitterHandleFromTwitterUrl(url) is deprecated, use avatarSource: \'gravatar\' instead')
        const twitter = url.match(/twitter\.com\/(\w+)/)
        if (twitter === null) {
            return undefined
        }
        return twitter[1]
    }

    static findAttribute(object, attribute) {
        if (!object) {
            return undefined
        }
        let match

        function iterateObject(target, name) {
            Object.keys(target).forEach(function(key) {
                if (isObject(target[key])) {
                    iterateObject(target[key], name)
                } else if (key === name) {
                    match = target[key]
                }
            })
        }

        iterateObject(object, attribute)

        return match ? match : undefined
    }
}

export {AvatarComponent}
